/* eslint-disable no-shadow */
/* eslint-disable camelcase */
//

import { useQuery } from '@axaou/ui'
import React from 'react'

import { getPhenotypeColumns } from '../PhenotypeList/PhenotypeTable'
import Phewas from '../PhenotypeList/Phewas'
import { axaouDevUrl, cacheEnabled, pouchDbName } from '../Query'
import { DocumentTitle, HalfPage, ScrollButton, ScrollButtonContainer, Spinner, StatusMessage, Titles as TitlesBase } from '../UserInterface'

import { useRecoilValue, useSetRecoilState } from 'recoil'
import styled from 'styled-components'
import {
  ancestryGroupAtom,
  AncestryGroupCodes,
  burdenSetAtom,
  geneIdAtom,
  hoveredAnalysisAtom,
  phewasOptsAtom,
  pValueTypeAtom,
  resultLayoutAtom,
  selectedAnalyses,
  selectedAnalysesColorsSelector,
  showSelectAnalysesOnlyAtom,
  useToggleSelectedAnalysis,
  mafSignificanceAtom,
  MafOption,
  MafSignificanceMap,
  AnnotationCategory,
  MafAnnotationSignificance,
  burdenTestSignificanceAtom,
  BurdenTestSignificanceMap,
} from '../sharedState'

import { annotateGenePhewasWithAnalysisMetadata } from '../PhenotypeList/phenotypeUtils'
import {
  AnalysisCategories,
  AnalysisMetadata,
  AxaouConfig,
  GeneAssociations,
  GeneModels,
  GenePhewasDataItem,
  LoadedAnalysis,
} from '../types'
import { filterValidAnalyses, getAvailableAnalysisIds, processGeneBurden } from '../utils'
import { useAppNavigation } from '../hooks/useAppNavigation'
import { GeneInfo } from './GeneInfo'

const hasGeneAssociationPvalues = (phenotype: GenePhewasDataItem & AnalysisMetadata): boolean => {
  return (
    phenotype.pvalue !== null || phenotype.pvalue_skat !== null || phenotype.pvalue_burden !== null
  )
}

interface Data {
  geneAssociations: GeneAssociations[]
  analysesMetadata: AnalysisMetadata[]
  geneModel: GeneModels[]
  categories: AnalysisCategories[]
  config: AxaouConfig
  availableAnalyses: LoadedAnalysis[];
}

const Titles = styled(TitlesBase)`
  align-items: flex-start;

  .side-nav-button {
    display: inline-block;
    padding: 0.35em 1.2em;
    border: 0.1em solid black;
    margin: 0 0.3em 0.3em 0;
    border-radius: 0.12em;
    box-sizing: border-box;
    text-decoration: none;
    font-family: 'Roboto', sans-serif;
    background-color: none;
    font-weight: 300;
    text-align: center;
    transition: all 0.2s;
    height: 3em;
    width: 3em;
  }
`

interface GenePhewasLayoutProps {
  uniquePhenotypes: (GenePhewasDataItem & AnalysisMetadata)[]
  categories: AnalysisCategories[]
  geneModel?: GeneModels
  ancestryGroup: AncestryGroupCodes
  onPointClick: any
  availableAncestries: AncestryGroupCodes[]
  size: { width: number; height: number }
}

const GenePhewasLayout = ({
  uniquePhenotypes,
  categories = [],
  geneModel,
  ancestryGroup,
  availableAncestries,
  onPointClick,
  size,
}: GenePhewasLayoutProps) => {
  const burdenSet = useRecoilValue(burdenSetAtom)
  const pValueType = useRecoilValue(pValueTypeAtom)
  const geneId = useRecoilValue(geneIdAtom)
  const selectedAnalysesList = useRecoilValue(selectedAnalyses)
  const toggleSelectedAnalysis = useToggleSelectedAnalysis()
  const setHoveredAnalysis = useSetRecoilState(hoveredAnalysisAtom)
  const analysesColors = useRecoilValue(selectedAnalysesColorsSelector)
  const showSelectAnalysesOnly = useRecoilValue(showSelectAnalysesOnlyAtom)
  const resultLayout = useRecoilValue(resultLayoutAtom)
  const showPhewasControls = useRecoilValue(phewasOptsAtom)

  const mediumBreakpointMinWidth = 500
  const mediumBreakpoint = showPhewasControls
    ? mediumBreakpointMinWidth + 350
    : mediumBreakpointMinWidth

  const baseColumns = [
    'description_with_link',
    'info',
    'pvalue',
    ...(ancestryGroup === 'meta' ? ['burden_direction'] : []),
    'mac_case',
    'mac_control',
    'select',
    'show',
  ]

  const mediumColumns = size.width > mediumBreakpoint
    ? [...(ancestryGroup === 'meta' ? [] : ['BETA']), 'n_cases', 'n_controls']
    : []

  const wideColumns =
    resultLayout === 'full' ? ['phenotype', 'trait_type', 'sex', 'category'] : []

  const columns = [...baseColumns, ...mediumColumns, ...wideColumns]

  const analyses = selectedAnalysesList

  const phenotypesInGeneColumns = getPhenotypeColumns({
    columns,
    analysisGroup: burdenSet,
    history,
    selectedAnalyses: analyses,
    toggleSelectedAnalysis,
    analysesColors,
    pValueType,
    showSelectAnalysesOnly,
  })

  const onHoverAnalysis = (analysisId: string) => {
    setHoveredAnalysis(analysisId)
  }

  const geneSymbol = geneModel?.symbol || geneId

  return (
    <>
      {geneModel && <GeneInfo geneIdentifier={geneSymbol || geneId || "Gene"} geneModel={geneModel} />}
      <DocumentTitle title={`${geneSymbol} | ${burdenSet} | All by All Browser`} />
      <Titles>
        <h3
          className="app-section-title gene-burden-phewas"
          style={{ width: "100%" }}
        >
          {uniquePhenotypes && uniquePhenotypes.length}{" "}
          <strong>{burdenSet}</strong> gene burden associations with{" "}
          <strong>{geneSymbol}</strong>
        </h3>
      </Titles>
      <Phewas
        uniquePhenotypes={uniquePhenotypes}
        categories={categories}
        columns={phenotypesInGeneColumns}
        onPointClick={onPointClick}
        exportFileName={`gene-phewas-exomes_${geneSymbol}`}
        enableExport
        showPlotTypeControls
        showAnalysisGroups
        showBurdenTestControls
        onHoverAnalysis={onHoverAnalysis}
        phewasType="gene"
        availableAncestries={availableAncestries}
        size={size}
      />
    </>
  )
}

interface Props { size: { width: number; height: number } }

const GenePhewasData: React.FC<Props> = ({ size }) => {
  const ancestryGroup = useRecoilValue(ancestryGroupAtom)
  const burdenSet = useRecoilValue(burdenSetAtom)
  const geneId = useRecoilValue(geneIdAtom)
  const { goToAssociation } = useAppNavigation()
  const setMafSignificance = useSetRecoilState(mafSignificanceAtom)
  const setBurdenTestSignificance = useSetRecoilState(burdenTestSignificanceAtom)

  const geneIdOrName = geneId

  const { queryStates, anyLoading } = useQuery<Data>({
    dbName: pouchDbName,
    queries: [
      {
        url: `${axaouDevUrl}/genes/phewas/${geneIdOrName}?gene_contract=burden_direction_v1`,
        name: 'geneAssociations',
      },
      { url: `${axaouDevUrl}/analyses?ancestry_group=${ancestryGroup}`, name: 'analysesMetadata' },
      {
        url: `${axaouDevUrl}/genes/model/${geneIdOrName}`,
        name: 'geneModel',
      },
      { url: `${axaouDevUrl}/categories`, name: 'categories' },
      { url: `${axaouDevUrl}/config`, name: 'config' },
      {
        url: `${axaouDevUrl}/analyses-loaded`,
        name: 'availableAnalyses',
      }
    ],
    deps: [geneIdOrName],
    cacheEnabled,
  })

  // Compute MAF and burden test significance for the controls - must be before conditional returns
  React.useEffect(() => {
    if (!queryStates?.geneAssociations?.data) {
      return
    }

    const geneAssociationsForAncestryData = processGeneBurden(
      queryStates.geneAssociations.data.filter((g) => g.ancestry_group === ancestryGroup)
    )

    if (geneAssociationsForAncestryData.length === 0) {
      return
    }

    const mapAnnotation = (annotation: string): AnnotationCategory | null => {
      if (annotation === 'pLoF') return 'pLoF'
      if (annotation === 'pLoF;missenseLC' || annotation === 'missenseLC' || annotation === 'missense') return 'missense'
      if (annotation === 'synonymous') return 'synonymous'
      return null
    }

    const significanceThreshold = 1e-4
    const isSignificant = (pvalue: number | null | undefined): boolean => {
      return pvalue !== null && pvalue !== undefined && pvalue > 0 && pvalue < significanceThreshold
    }

    const mafOptions: MafOption[] = [0.01, 0.001, 0.0001]
    const defaultAnnotSig: MafAnnotationSignificance = { pLoF: 'none', missense: 'none', synonymous: 'none' }
    const newSignificance: MafSignificanceMap = {
      0.01: { ...defaultAnnotSig },
      0.001: { ...defaultAnnotSig },
      0.0001: { ...defaultAnnotSig },
    }

    // Track burden test type significance per annotation category
    const defaultBurdenAnnotSig = { pLoF: 'none' as const, missense: 'none' as const, synonymous: 'none' as const }
    const newBurdenSig: BurdenTestSignificanceMap = {
      burden: { ...defaultBurdenAnnotSig },
      skat: { ...defaultBurdenAnnotSig },
      skato: { ...defaultBurdenAnnotSig },
    }

    mafOptions.forEach((maf) => {
      const resultsForMaf = geneAssociationsForAncestryData.filter((g) => g.max_maf === maf)

      resultsForMaf.forEach((r) => {
        const category = mapAnnotation(r.annotation)
        if (!category) return

        const pvalues = [r.pvalue, r.pvalue_burden, r.pvalue_skat]
        const hasSignificantHit = pvalues.some(isSignificant)

        if (hasSignificantHit) {
          newSignificance[maf][category] = 'hit'
        }

        // Check each test type individually per annotation category
        if (isSignificant(r.pvalue_burden)) {
          newBurdenSig.burden[category] = 'hit'
        }
        if (isSignificant(r.pvalue_skat)) {
          newBurdenSig.skat[category] = 'hit'
        }
        if (isSignificant(r.pvalue)) {
          newBurdenSig.skato[category] = 'hit'
        }
      })
    })

    setMafSignificance(newSignificance)
    setBurdenTestSignificance(newBurdenSig)
  }, [queryStates?.geneAssociations?.data, ancestryGroup, setMafSignificance, setBurdenTestSignificance])

  const Container = HalfPage

  if (anyLoading()) {
    return (
      <Container>
        <Spinner />
      </Container>
    )
  }

  if (anyLoading()) {
    return (
      <Container>
        <Spinner />
      </Container>
    )
  }

  if (!queryStates) {
    return (
      <Container>
        <StatusMessage>No Data Found</StatusMessage>
      </Container>
    )
  }

  const { analysesMetadata, geneModel, categories, geneAssociations } = queryStates

  const availableAncestries: AncestryGroupCodes[] =
    (geneAssociations.data &&
      Array.from(
        new Set(geneAssociations.data.map((gene) => gene.ancestry_group as AncestryGroupCodes))
      )) ||
    []

  const geneAssociationsForAncestry = processGeneBurden(
    (geneAssociations.data &&
      geneAssociations.data.filter((g) => g.ancestry_group === ancestryGroup && g.annotation === burdenSet)) ||
    []
  )

  // if (!geneAssociationsForAncestry) {
  //   throw new Error(`Gene Phewas not found for ancestry group: ${ancestryGroup.toUpperCase()}`)
  // }

  const availableAnalysesState = queryStates.availableAnalyses;

  const uniquePhenotypes = filterValidAnalyses(
    annotateGenePhewasWithAnalysisMetadata(
      geneAssociationsForAncestry,
      analysesMetadata.data || []
    ),
    getAvailableAnalysisIds(availableAnalysesState?.data)
  ).filter(hasGeneAssociationPvalues)


  const onPointClick = ({ analysis_id }: GenePhewasDataItem) => {
    goToAssociation(analysis_id, { geneId: geneId || null })
  }

  return (
    <Container>
      {/* @ts-ignore */}
      <GenePhewasLayout
        uniquePhenotypes={uniquePhenotypes}
        categories={categories.data || []}
        geneModel={geneModel && geneModel.data && geneModel.data[0] || undefined}
        ancestryGroup={ancestryGroup}
        availableAncestries={availableAncestries}
        onPointClick={onPointClick}
        size={size}
      />
    </Container>
  )
}

export default GenePhewasData
